import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/Seo";
import Editable from "@/components/Editable";
import EditableSection from "@/components/blocks/EditableSection";
import PostcodeSearch from "@/components/PostcodeSearch";
import { SERVICES } from "@/data/services";
import { GraduationCap, House, PersonSimpleWalk, Coffee, HeartStraight, PawPrint, Briefcase, ArrowRight, MapPin, Phone, Check } from "@phosphor-icons/react";

const ICONS = { GraduationCap, House, PersonSimpleWalk, Coffee, HeartStraight, PawPrint };
const HERO_IMG = "https://images.unsplash.com/photo-1518981154746-fdc34f7b0548?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Services() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Puppy Training Services Sunshine Coast | Puppl"
        description="Puppy school ($149 AUD), teenage puppy school, private puppy consults, walk & trains, home check-ins, therapy dog training and scent detection."
        canonical={typeof window !== "undefined" ? window.location.origin + "/services" : ""}
        schema={[
          ORG_SCHEMA,
          breadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: "Services", url: typeof window !== "undefined" ? window.location.origin + "/services" : "/services" },
          ]),
        ]}
      />
      <PublicHeader />
      <EditableSection slug="services" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img loading="lazy" src={HERO_IMG} alt="Puppy training Sunshine Coast" className="w-full h-full object-cover" />
          <div className="absolute inset-0 pp-gradient-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-36">
          <div className="max-w-3xl text-white">
            <Editable id="services.hero.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3 fade-up" defaultHtml="Our services" />
            <div className="grid lg:grid-cols-[1fr_auto] gap-6 lg:items-end mb-6 fade-up fade-up-1">
              <Editable id="services.hero.title" as="h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" defaultHtml="Puppy training for<br />every stage &amp; budget." testid="services-hero-title" />
              <div className="lg:pb-2 lg:min-w-[280px]"><PostcodeSearch /></div>
            </div>
            <Editable id="services.hero.subtitle" as="p" className="text-lg lg:text-xl opacity-90 leading-relaxed max-w-xl fade-up fade-up-2" defaultHtml="Pick the service that suits you — or chat with the team&#39;s virtual receptionist to find the right fit." />
          </div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-6">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || GraduationCap;
            return (
              <article key={s.slug} className="card-pp p-8 flex flex-col" data-testid={`service-card-${s.slug}`}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}15` }}>
                    <Icon size={30} weight="duotone" style={{ color: s.color }} />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading text-2xl font-bold text-stone-900">
                      <Link to={`/services/${s.slug}`} className="hover:text-[#f38838] transition-colors">{s.title}</Link>
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-stone-600 mt-1">
                      <span className="font-semibold" style={{ color: s.color }}>{s.price}</span>
                      <span className="text-stone-400">•</span>
                      <span>{s.duration}</span>
                    </div>
                  </div>
                </div>
                <p className="text-stone-700 leading-relaxed mb-5">{s.desc}</p>

                <ul className="space-y-2 mb-5">
                  {s.bullets.slice(0, 4).map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-stone-700">
                      <Check size={16} weight="bold" className="text-[#1b4a76] mt-1 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {s.locations && (
                  <div className="bg-[#FDFBF7] border border-stone-200 rounded-xl p-4 mb-5">
                    <div className="font-heading font-semibold text-sm text-stone-900 mb-2 flex items-center gap-2">
                      <MapPin size={16} weight="duotone" className="text-[#f38838]" /> Locations
                    </div>
                    <ul className="text-xs text-stone-600 space-y-1">
                      {s.locations.map((l, k) => <li key={k}>• {l}</li>)}
                    </ul>
                  </div>
                )}

                <div className="mt-auto flex gap-3">
                  <Link to={`/services/${s.slug}`} className="btn-primary text-sm inline-flex items-center gap-2" data-testid={`service-cta-${s.slug}`}>
                    Learn more <ArrowRight size={16} weight="bold" />
                  </Link>
                  <a href="mailto:support@puppl.app" className="text-sm font-semibold text-stone-700 hover:text-[#f38838] inline-flex items-center px-3 py-2.5">
                    Enquire
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CONTACT BAR */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto bg-[#1b4a76] text-white rounded-3xl p-10 lg:p-14 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <Editable id="services.contactbar.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-2" defaultHtml="Have questions?" />
            <Editable id="services.contactbar.heading" as="h2" className="font-heading text-2xl lg:text-3xl font-bold" defaultHtml="Get in touch with the team directly." />
            <Editable id="services.contactbar.body" as="p" className="opacity-80 mt-2 text-sm" defaultHtml="Call, email, or chat with our virtual receptionist for instant answers." />
          </div>
          <div className="space-y-3">
            <a href="tel:0435740077" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition-colors" data-testid="contact-phone">
              <Phone size={20} weight="duotone" className="text-[#E2B054]" />
              <div>
                <div className="text-xs opacity-70">Call</div>
                <div className="font-semibold">0435 740 077</div>
              </div>
            </a>
            <a href="mailto:support@puppl.app" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition-colors" data-testid="contact-email">
              <Briefcase size={20} weight="duotone" className="text-[#E2B054]" />
              <div>
                <div className="text-xs opacity-70">Email</div>
                <div className="font-semibold text-sm">support@puppl.app</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
